import { useState } from 'react';
import { Check, ChevronDown, ChevronRight, SlidersHorizontal, Trash2 } from 'lucide-react';
import { CHOOSABLE_UNITS, effectiveSettings, girthEditable, girthLabel, isSprayed, lineQuantity, METHOD_FIELDS, paintStatesComplete, rateUnit, SPEED_PRESETS, surfaceLabel, surfaceTotals, unitChoosable } from '@higgins/m2q-pricing';
import type { MeasuredSurface, PaintFlag, PaintStates, SurfaceLine, SurfaceSettings, SurfaceType } from '@higgins/m2q-pricing';
import type { ShownPhoto } from '../lib/area-photos';
import { LINE_GRID, LineRow } from './line-row';
import { PhotoStrip } from './photo-strip';
import { AddAction } from './add-action';
import { RemoveButton } from './remove-button';
import { NumberField } from './number-field';
import { completionEdgeColour } from './completion-style';
import { formatHours, formatQuantity } from './surface-format';
import { PaintFlagSetting, SurfaceName } from './surface-naming';
import { SwipeToReveal } from './swipe-to-reveal';

interface SurfaceCardProps {
  surface: MeasuredSurface;
  /** Undefined when the type is no longer in CRM's active list (or not yet on this device). */
  type: SurfaceType | undefined;
  /** Photos taken of this surface. */
  photos: ShownPhoto[];
  jobId: string;
  areaId: string;
  /** Its repeat group's count (1 when not in a group): totals are multiplied by it. */
  count: number;
  /** The area is wash only (#510): measured, shown greyed, not priced. */
  unpriced?: boolean;
  /** Its non-standard paint flag and the desk's paint systems, each done or not (#666). */
  paintStates: PaintStates;
  onAddLine: () => void;
  onUpdateLine: (lineId: string, change: Partial<SurfaceLine>) => void;
  onRemoveLine: (lineId: string) => void;
  onSettings: (settings: SurfaceSettings) => void;
  /** A custom line's unit ("Other" only, #417). */
  onUnit: (unit: string) => void;
  /** This job's name for the surface; blank goes back to the type's. */
  onRename: (name: string) => void;
  onPaintFlag: (flag: PaintFlag | undefined) => void;
  /** Called once the measurer has confirmed it. */
  onRemove: () => void;
  /** Open shows its lines; closed is its header line only (Done, #416). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** A setting the measurer may override: its value, the type's default, and a way back to it. */
function SettingField({ label, value, fallback, onChange }: { label: string; value: number | undefined; fallback: number | null | undefined; onChange: (v: number | undefined) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="whitespace-nowrap">{label}</span>
      <span className="w-20">
        <NumberField label={label} value={value ?? fallback} onChange={(v) => onChange(v ?? undefined)} />
      </span>
      {/* The link's room is always kept, so it appearing never moves the fields after it (#389). */}
      <span className="w-20 shrink-0">
        {value !== undefined && value !== fallback && (
          <button type="button" onClick={() => onChange(undefined)} className="whitespace-nowrap text-xs text-slate-500 underline">
            default {fallback ?? '—'}
          </button>
        )}
      </span>
    </label>
  );
}

/** Brush and roll, or spray where the surface type has a spray rate (SCHEMA.md "Before UAT" §2). */
function ApplicationChoice({ type, unit, sprayed, onSettings }: { type: SurfaceType; unit: string; sprayed: boolean; onSettings: (settings: SurfaceSettings) => void }) {
  const option = (on: boolean) => `h-9 rounded-full border px-3 text-sm ${on ? 'border-primary bg-orange-50 text-orange-800' : 'border-slate-300 text-slate-600'}`;
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="How it's painted">
      <button type="button" aria-pressed={!sprayed} onClick={() => onSettings({ application: undefined, rate: undefined })} className={option(!sprayed)}>
        Brush / roll
      </button>
      <button type="button" aria-pressed={sprayed} onClick={() => onSettings({ application: 'spray', rate: undefined })} className={option(sprayed)}>
        Spray {type.sprayRate} {rateUnit(unit)}{type.sprayUplift ? ` +${type.sprayUplift}% time` : ''}
      </button>
    </div>
  );
}

/** m², LM or Each, for a custom line ("Other", #417). */
function UnitChoice({ unit, onUnit }: { unit: string; onUnit: (unit: string) => void }) {
  const option = (on: boolean) => `h-9 min-w-14 rounded-full border px-3 text-sm ${on ? 'border-primary bg-orange-50 text-orange-800' : 'border-slate-300 text-slate-600'}`;
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Measured in">
      <span className="mr-1 text-sm">Measured in</span>
      {CHOOSABLE_UNITS.map((u) => (
        <button key={u} type="button" aria-pressed={unit === u} onClick={() => unit !== u && onUnit(u)} className={option(unit === u)}>
          {u}
        </button>
      ))}
    </div>
  );
}

/** "Remove Fascia from this area? Its 3 measuring lines go with it." */
function removeQuestion(surface: MeasuredSurface) {
  const lines = surface.lines.length;
  return (
    <>
      Remove <strong>{surfaceLabel(surface)}</strong> from this area?
      {lines > 0 && ` Its ${lines === 1 ? 'measuring line goes' : `${lines} measuring lines go`} with it.`}
    </>
  );
}

export function SurfaceCard({ surface, type, photos, jobId, areaId, count, unpriced, paintStates, onAddLine, onUpdateLine, onRemoveLine, onSettings, onUnit, onRename, onPaintFlag, onRemove, open, onOpenChange }: SurfaceCardProps) {
  const [showSettings, setShowSettings] = useState(false);
  const totals = surfaceTotals(surface, type, count);
  const settings = effectiveSettings(surface, type);
  const sprayed = isSprayed(surface, type);
  const fields = METHOD_FIELDS[surface.method];
  const rate = rateUnit(surface.unit);
  const paintComplete = paintStatesComplete(paintStates);

  const name = surfaceLabel(surface);
  const question = removeQuestion(surface);

  return (
    <SwipeToReveal
      behind={
        <RemoveButton question={question} onRemove={onRemove} label={`Delete ${name}`} className="flex h-full w-full items-start justify-center pt-3.5 text-sm font-medium text-white">
          Delete
        </RemoveButton>
      }
    >
      <div className={`rounded-lg border border-slate-200 ${paintComplete === undefined ? '' : `border-l-4 ${completionEdgeColour(paintComplete)}`} ${unpriced ? 'bg-slate-50' : 'bg-white'}`}>
        <div className="flex min-h-12 w-full items-center justify-between gap-2 px-3">
          <SurfaceName surface={surface} sprayed={sprayed} paintStates={paintStates} onRename={onRename} />
          <button type="button" onClick={() => onOpenChange(!open)} aria-expanded={open} aria-label={`${open ? 'Close' : 'Open'} ${name}`} className={`flex min-h-12 flex-1 items-center justify-end gap-1 text-sm tabular-nums ${unpriced ? 'text-slate-400' : 'text-slate-700'}`}>
            {formatQuantity(totals.quantity)} {surface.unit} · {unpriced ? <span className="italic">not priced: wash only</span> : <span className="font-medium">{formatHours(totals.hours)}</span>}
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </div>

        {open && (
          <div className="border-t border-slate-100 pb-2">
            {!type && <p className="px-3 pt-2 text-xs text-amber-700">This surface type isn&rsquo;t in the current list — hours use only the values set here.</p>}
            {(unitChoosable(type) || girthEditable(surface, type)) && (
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-3 pt-2">
                {unitChoosable(type) && <UnitChoice unit={surface.unit} onUnit={onUnit} />}
                {girthEditable(surface, type) && (
                  <SettingField label={girthLabel(surface, type)} value={surface.girth} fallback={type?.girthDefault} onChange={(girth) => onSettings({ girth })} />
                )}
              </div>
            )}
            {fields && (
              <div className={`${LINE_GRID} pt-2 text-xs text-slate-500`}>
                <span />
                <span>{fields.a}</span>
                <span>{fields.b ?? ''}</span>
                <span>Qty</span>
                <span className={fields.sides ? 'hidden w-[5.75rem] lg:block' : 'hidden lg:block'}>{fields.sides ? 'Sides' : ''}</span>
                <span className="hidden text-right lg:block">{surface.unit}</span>
              </div>
            )}
            {surface.lines.map((line, i) => (
              <LineRow
                key={line.id}
                method={surface.method}
                line={line}
                index={i}
                quantity={lineQuantity(surface.method, line, settings.girth)}
                onChange={(change) => onUpdateLine(line.id, change)}
                onRemove={() => onRemoveLine(line.id)}
              />
            ))}
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 pt-1 text-sm text-slate-600">
              <AddAction onClick={onAddLine}>Add line</AddAction>
              {/* Settings and the bin kept well apart: a click meant for Settings removed a surface (#415). */}
              <span className="flex items-center gap-8">
                <button type="button" onClick={() => setShowSettings(!showSettings)} className="flex min-h-11 items-center gap-1">
                  <SlidersHorizontal className="h-4 w-4" /> Settings
                </button>
                <RemoveButton question={question} onRemove={onRemove} label={`Remove ${name}`} className="flex h-11 w-11 items-center justify-center text-slate-400 hover:text-red-700">
                  <Trash2 className="h-4 w-4" />
                </RemoveButton>
              </span>
            </div>

            <div className="px-3 pt-2">
              <PhotoStrip jobId={jobId} areaId={areaId} surface={{ id: surface.id, name }} photos={photos} />
            </div>

            {showSettings && (
              <div className="mx-3 mt-1 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-slate-100 pt-2">
                <PaintFlagSetting flag={surface.paintFlag} onChange={onPaintFlag} />
                {typeof type?.sprayRate === 'number' && type.sprayRate > 0 && <ApplicationChoice type={type} unit={surface.unit} sprayed={sprayed} onSettings={onSettings} />}
                <SettingField label="Coats" value={surface.coats} fallback={type?.defaultCoats} onChange={(coats) => onSettings({ coats })} />
                <SettingField label={`Production rate ${rate}`} value={surface.rate} fallback={sprayed ? type?.sprayRate : type?.productionRate} onChange={(rate) => onSettings({ rate })} />
                <div className="flex flex-wrap gap-1.5" role="group" aria-label="Speed presets">
                  {SPEED_PRESETS.map((p) => (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => onSettings({ rate: p.rate })}
                      className={`h-9 rounded-full border px-3 text-sm ${settings.rate === p.rate ? 'border-primary bg-orange-50 text-orange-800' : 'border-slate-300 text-slate-600'}`}
                    >
                      {p.label} {p.rate}
                    </button>
                  ))}
                </div>
                <SettingField label={`Prep rate ${rate}`} value={surface.prep} fallback={type?.prepRate} onChange={(prep) => onSettings({ prep })} />
                <SettingField label="Spread rate m²/L" value={surface.spread} fallback={type?.spreadRate} onChange={(spread) => onSettings({ spread })} />
                <span className="text-xs text-slate-500">Litres {formatQuantity(totals.litres)}</span>
              </div>
            )}

            <div className="flex justify-end px-3 pt-2">
              <button type="button" onClick={() => onOpenChange(false)} className="flex min-h-11 items-center gap-1 rounded-md border border-slate-300 px-4 text-sm font-medium text-slate-800">
                <Check className="h-4 w-4" /> Done
              </button>
            </div>
          </div>
        )}
      </div>
    </SwipeToReveal>
  );
}
